const KEYS = {
  accessToken: 'impersonation.accessToken',
  refreshToken: 'impersonation.refreshToken',
  userId: 'impersonation.userId',
  userEmail: 'impersonation.userEmail',
  userFirstName: 'impersonation.userFirstName',
  userLastName: 'impersonation.userLastName',
  userRole: 'impersonation.userRole',
  companyName: 'impersonation.companyName',
  adminName: 'impersonation.adminName',
} as const;

export interface ImpersonationPayload {
  accessToken: string;
  refreshToken: string;
  userId: string;
  userEmail: string;
  userRole: string;
  userFirstName?: string;
  userLastName?: string;
  companyName?: string;
  adminName?: string;
}

export interface ImpersonationUser {
  userId: string;
  userEmail: string;
  userRole: string;
  userFirstName?: string;
  userLastName?: string;
  companyName?: string;
  adminName?: string;
}

export function isImpersonating(): boolean {
  return sessionStorage.getItem(KEYS.accessToken) !== null;
}

export function getImpersonationToken(): string | null {
  return sessionStorage.getItem(KEYS.accessToken);
}

const readNonNull = (key: string): string | null => {
  const v = sessionStorage.getItem(key);
  if (v === null || v === 'null' || v === 'undefined' || v === '') return null;
  return v;
};

export function getImpersonationUser(): ImpersonationUser | null {
  const userId = readNonNull(KEYS.userId);
  const userRole = readNonNull(KEYS.userRole);
  if (!userId || !userRole) return null;
  const user: ImpersonationUser = {
    userId,
    userEmail: readNonNull(KEYS.userEmail) ?? userId,
    userRole,
  };
  const userFirstName = readNonNull(KEYS.userFirstName);
  if (userFirstName) user.userFirstName = userFirstName;
  const userLastName = readNonNull(KEYS.userLastName);
  if (userLastName) user.userLastName = userLastName;
  const companyName = readNonNull(KEYS.companyName);
  if (companyName) user.companyName = companyName;
  const adminName = readNonNull(KEYS.adminName);
  if (adminName) user.adminName = adminName;
  return user;
}

export function writeImpersonationSession(payload: ImpersonationPayload): void {
  const safe = (v: unknown): string => (v == null ? '' : typeof v === 'string' ? v : String(v));
  sessionStorage.setItem(KEYS.accessToken, safe(payload.accessToken));
  sessionStorage.setItem(KEYS.refreshToken, safe(payload.refreshToken));
  sessionStorage.setItem(KEYS.userId, safe(payload.userId));
  sessionStorage.setItem(KEYS.userEmail, safe(payload.userEmail));
  sessionStorage.setItem(KEYS.userRole, safe(payload.userRole));
  if (payload.userFirstName) {
    sessionStorage.setItem(KEYS.userFirstName, safe(payload.userFirstName));
  }
  if (payload.userLastName) {
    sessionStorage.setItem(KEYS.userLastName, safe(payload.userLastName));
  }
  if (payload.companyName) {
    sessionStorage.setItem(KEYS.companyName, safe(payload.companyName));
  }
  if (payload.adminName) {
    sessionStorage.setItem(KEYS.adminName, safe(payload.adminName));
  }
}

export function clearImpersonationSession(): void {
  Object.values(KEYS).forEach(k => sessionStorage.removeItem(k));
}

export function getAdminOrigin(): string {
  const configured = (import.meta.env.VITE_ADMIN_APP_URL as string | undefined)?.trim();
  if (configured) {
    return configured.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location) {
    const { hostname, protocol, port } = window.location;
    const portSuffix = port ? `:${port}` : '';

    if (hostname.startsWith('client-') && hostname.endsWith('.directhomeservice.com')) {
      const suffix = hostname.slice('client-'.length);
      return `${protocol}//admin-${suffix}${portSuffix}`;
    }
    if (hostname === 'client-app-develop.directhomeservice.com') {
      return 'https://admin-develop.directhomeservice.com';
    }
    if (hostname === 'client-app-staging.directhomeservice.com') {
      return 'https://admin-staging.directhomeservice.com';
    }
    if (hostname === 'client.directhomeservice.com') {
      return 'https://admin.directhomeservice.com';
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:3002`;
    }
  }
  return '';
}

export function isAllowedAdminOrigin(origin: string): boolean {
  const expected = getAdminOrigin();
  if (expected && origin === expected) return true;
  try {
    const url = new URL(origin);
    const host = url.hostname;
    if (host.startsWith('admin-') && host.endsWith('.directhomeservice.com')) return true;
    if (
      host === 'admin.directhomeservice.com' ||
      host === 'admin-develop.directhomeservice.com' ||
      host === 'admin-staging.directhomeservice.com'
    )
      return true;
    if (host === 'localhost' || host === '127.0.0.1') return true;
  } catch {
    return false;
  }
  return false;
}
